import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '../config/api';
import Loader3D from './Loader3D';

export default function AdvancedSearch({ onSearch, className = '' }) {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const abortControllerRef = useRef(null);

  const debouncedFetch = useCallback(async (term) => {
    if (term.length < 2) {
      setSuggestions([]);
      setError(null);
      return;
    }

    try {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      setLoading(true);
      setError(null);

      const response = await apiClient.get('/posts/search/suggestions', {
        params: { q: term },
        signal: abortControllerRef.current.signal,
        cache: true // Enable caching for suggestions
      });

      if (response.data?.suggestions) {
        setSuggestions(response.data.suggestions);
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      
      console.error('Error fetching suggestions:', error);
      setError('Failed to fetch suggestions. Please try again.');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => debouncedFetch(searchTerm), 300);
    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [searchTerm, debouncedFetch]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
      onSearch?.(searchTerm.trim());
      setIsFocused(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    navigate(`/post/${suggestion._id}`);
    setSearchTerm('');
    setSuggestions([]);
    setIsFocused(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsFocused(false);
    }
  };

  return (
    <div ref={searchRef} className={`relative w-64 ml-auto mb-6 ${className}`}>
      <form onSubmit={handleSubmit}>
        <motion.div
          className={`relative rounded-xl transition-all duration-200 ${
            isFocused ? 'ring-2 ring-primary-500 shadow-lg' : 'shadow-sm'
          }`}
          whileTap={{ scale: 0.995 }}
        >
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onKeyDown={handleKeyDown}
            className="w-full px-4 py-2 pr-10 bg-white dark:bg-gray-800 border-2 border-transparent 
                     rounded-xl focus:outline-none dark:text-white placeholder-gray-400
                     dark:placeholder-gray-500 text-sm"
            placeholder="Search posts..."
            aria-label="Search posts"
            autoComplete="off"
          />
          <button
            type="submit"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 text-gray-400 
                     hover:text-primary-500 transition-colors duration-200"
            aria-label="Submit search"
          >
            <motion.span whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </motion.span>
          </button>
        </motion.div>
      </form>

      <AnimatePresence>
        {isFocused && (searchTerm.length >= 2 || suggestions.length > 0) && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-xl 
                      shadow-xl z-10 border border-gray-100 dark:border-gray-700"
          >
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <Loader3D size="sm" />
              </div>
            ) : error ? (
              <div className="p-4 text-center text-red-500 text-sm">
                {error}
              </div>
            ) : suggestions.length > 0 ? (
              <div className="py-2">
                {suggestions.map((suggestion) => (
                  <motion.button
                    key={suggestion._id}
                    whileHover={{ backgroundColor: 'rgba(0,0,0,0.05)' }}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700
                             focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <p className="font-medium text-gray-900 dark:text-white">{suggestion.title}</p>
                    {suggestion.excerpt && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                        {suggestion.excerpt}
                      </p>
                    )}
                  </motion.button>
                ))}
                <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
                  <button
                    onClick={handleSubmit}
                    className="text-primary-500 hover:text-primary-600 text-sm font-medium
                             focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-lg px-2 py-1"
                  >
                    View all results for "{searchTerm}"
                  </button>
                </div>
              </div>
            ) : searchTerm.length >= 2 ? (
              <div className="p-4 text-center text-gray-500">
                No results found for "{searchTerm}"
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
} 